/**
 * @fileoverview ShowdownsPage — Discover, enter, and track showdowns (authenticated)
 *
 * This page serves two purposes at once — it is NOT just "games I've entered":
 *   1. Discovery: any public live/entries-open or opening-soon showdown shows
 *      up here so a logged-in user can find and enter it, exactly like the
 *      public landing page (same underlying game_slates fetch — see
 *      utils/showdownStatus.js buildShowdownItems/filterShowdownsForTab).
 *   2. Tracking: "My Entries"/"Completed" scope to showdowns the user has
 *      actually entered, including ones no longer publicly enterable (e.g.
 *      "results pending" games with entries closed).
 *
 * Card status/routing/progress all come from the single shared helper
 * `getShowdownDisplayState` — see utils/showdownStatus.js for the full
 * lifecycle rules. Don't re-derive status here.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate }        from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection, query, where, orderBy, onSnapshot, doc, getDoc, getDocs,
} from 'firebase/firestore';
import { auth, db }  from '../services/firebase';
import {
  toMs, buildShowdownItems, filterShowdownsForTab,
} from '../utils/showdownStatus';
import { getEntryEarnings } from '../utils/stableStatus';
import { subscribePublicUpcomingSchedule } from '../services/upcomingMeetingsService';
import PlannedMeetingCard from '../components/PlannedMeetingCard';
import './ShowdownsPage.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtTime(val) {
  const ms = toMs(val);
  if (!ms) return null;
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(ms));
}

function formatDateLabel(val) {
  const ms = toMs(val);
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long',
  }).toUpperCase();
}

// "4 July 2026" — Completed tab table date column.
function formatCompletedDate(val) {
  const ms = toMs(val);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

// Real-money prize pool for display on the card.
// Priority 1: new dedicated playerPrizePool field (number).
// Priority 2: legacy prizes array (summed dollar amounts from descriptions).
function getDisplayPrizePool(slate) {
  if (slate.playerPrizePool && slate.playerPrizePool > 0) {
    return slate.playerPrizePool;
  }
  if (slate.prizes && slate.prizes.length > 0) {
    const total = slate.prizes.reduce((sum, p) => {
      if (!p.description) return sum;
      const num = parseFloat(p.description.replace(/[^0-9.]/g, ''));
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
    if (total > 0) return total;
  }
  return 0;
}

// ─── Countdown (live-ticking wrapper around state.countdownLabel) ────────────

function useTicker(intervalMs = 30000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

// ─── Premium Showdown Card ────────────────────────────────────────────────────

const RaceProgress = ({ state }) => {
  if (state.totalRaceCount === 0) return null;
  return (
    <div className="sd-card-progress">
      <div className="sd-card-progress-header">
        <span>Race Progress</span>
        <span>{state.resultedRaceCount} / {state.totalRaceCount} resulted</span>
      </div>
      <div className="sd-card-progress-track">
        <div
          className={`sd-card-progress-fill ${state.isCompleted ? 'sd-card-progress-fill--done' : ''}`}
          style={{ width: `${state.progressPct}%` }}
        />
      </div>
    </div>
  );
};

const ShowdownCard = ({ slate, state, myEntryCount, navigate }) => {
  // Re-render every 30s so countdowns stay roughly fresh without a full
  // per-second timer for every card on the page.
  useTicker();

  const closingSoon = state.lifecycle === 'live'
    && toMs(slate.gameCloseTime)
    && (toMs(slate.gameCloseTime) - Date.now()) < 15 * 60 * 1000;

  const isClickable = !state.primaryActionDisabled && !!state.primaryActionRoute;

  const handleCardClick = () => {
    if (isClickable) navigate(state.primaryActionRoute);
  };

  const handlePrimaryClick = (e) => {
    e.stopPropagation();
    if (state.primaryActionRoute) navigate(state.primaryActionRoute);
  };

  const handleBoardClick = (e) => {
    e.stopPropagation();
    if (state.secondaryActionRoute) navigate(state.secondaryActionRoute);
  };

  const statusBadgeText = state.lifecycle === 'openingSoon' || state.lifecycle === 'entriesOpen'
    ? `● ${state.statusLabel}`
    : state.lifecycle === 'live'
      ? '● LIVE'
      : state.statusLabel;

  return (
    <div
      className={[
        'sd-card',
        `sd-card--${state.statusTone}`,
        isClickable ? '' : 'sd-card--static',
      ].join(' ').trim()}
      onClick={handleCardClick}
    >
      {/* Image header — dark racecourse background + status/entered badges */}
      <div className="sd-card-image">
        <img src="/track-wp.png" alt="" />
        <div className="sd-card-image-overlay" />

        {myEntryCount > 0 && (
          <span className="sd-card-badge sd-card-badge--entered">
            ✓ {myEntryCount > 1 ? `${myEntryCount} ENTRIES` : 'ENTERED'}
          </span>
        )}

        <span className={`sd-card-badge sd-card-badge--status sd-card-badge--${state.statusTone}`}>
          {statusBadgeText}
        </span>

        {closingSoon && (
          <span className="sd-card-badge sd-card-badge--closing">⚡ CLOSING SOON</span>
        )}

        <div className="sd-card-image-title">
          <div className="sd-card-image-date">
            {formatDateLabel(slate.gameOpenTime || slate.meetingDate)}
          </div>
          <h3 className="sd-card-name">{slate.gameName || slate.id}</h3>
        </div>
      </div>

      {/* Body */}
      <div className="sd-card-body">

        {state.countdownLabel && (
          <div className={`sd-card-countdown sd-card-countdown--${state.statusTone}`}>
            ⏱ {state.countdownLabel}
          </div>
        )}

        <div className="sd-card-timing">
          {state.lifecycle === 'live' && (
            <span className="sd-timing-item">
              <span className="sd-timing-label">Closes</span>
              <span className="sd-timing-val">{fmtTime(slate.gameCloseTime)}</span>
            </span>
          )}
          {(state.isResultsPending || state.isCompleted) && (
            <span className="sd-timing-item">
              <span className="sd-timing-label">Ended</span>
              <span className="sd-timing-val">{fmtTime(slate.gameCloseTime)}</span>
            </span>
          )}
          <span className="sd-timing-item">
            <span className="sd-timing-label">Prize Pool</span>
            <span className="sd-timing-val sd-timing-val--gold">
              ${getDisplayPrizePool(slate).toLocaleString('en-AU', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </span>
          {slate.totalRaces > 0 && (
            <span className="sd-timing-item">
              <span className="sd-timing-label">Races</span>
              <span className="sd-timing-val">{slate.totalRaces}</span>
            </span>
          )}
        </div>

        {/* Race progress — always shown for games with configured races. */}
        <RaceProgress state={state} />

        {/* Actions — primary CTA + secondary Board, both driven entirely by
            getShowdownDisplayState. A completed/results-pending game never
            routes to /slate; an opening-soon game never routes anywhere. */}
        <div className="sd-card-actions">
          <button
            className={`sd-btn sd-btn--${state.statusTone === 'live' ? 'gold' : state.isCompleted ? 'outline' : 'primary'} ${state.primaryActionDisabled ? 'sd-btn--disabled' : ''} ${closingSoon ? 'sd-btn--pulse' : ''}`}
            disabled={state.primaryActionDisabled}
            onClick={handlePrimaryClick}
            type="button"
          >
            {state.primaryActionLabel}{!state.primaryActionDisabled && state.primaryActionRoute ? ' →' : ''}
          </button>

          <span
            className="sd-board-btn-wrap"
            title={state.secondaryActionHelperText || undefined}
          >
            <button
              className={`sd-btn sd-btn--board ${state.secondaryActionDisabled ? 'sd-btn--disabled' : ''}`}
              disabled={state.secondaryActionDisabled}
              onClick={handleBoardClick}
              type="button"
            >
              {state.secondaryActionLabel} {!state.secondaryActionDisabled && '→'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Completed tab — table, not cards ─────────────────────────────────────────
// A player's completed-games history reads better as a scannable table than
// a card grid (see "Completed Showdowns Tab" fix) — Game/Date/Rank/Score/
// Races/ELO Change, newest first, click a row to open that game's board.

function CompletedRow({ slate, entry, rank, eloDelta, navigate }) {
  const races = `${entry?.racesSettled ?? 0}/${slate.totalRaces ?? 0}`;
  const rankClass = rank === 1 ? 'completed-table__rank--1'
    : rank === 2 ? 'completed-table__rank--2'
    : rank === 3 ? 'completed-table__rank--3'
    : '';

  return (
    <tr onClick={() => navigate(`/leaderboard/${slate.id}`)}>
      <td className="completed-table__game">{slate.gameName || slate.id}</td>
      <td>{formatCompletedDate(slate.meetingDate || slate.gameOpenTime)}</td>
      <td className={rankClass}>{rank ? `#${rank}` : '—'}</td>
      <td className="completed-table__score">
        ${Math.round(getEntryEarnings(entry)).toLocaleString('en-AU')}
      </td>
      <td>{races}</td>
      <td>
        {eloDelta == null ? (
          <span className="completed-table__elo--flat">—</span>
        ) : (
          <span className={eloDelta >= 0 ? 'completed-table__elo--up' : 'completed-table__elo--down'}>
            {eloDelta > 0 ? '+' : ''}{eloDelta} {eloDelta >= 0 ? '▲' : '▼'}
          </span>
        )}
      </td>
    </tr>
  );
}

function CompletedShowdownsTable({ items, entries, currentUser, navigate }) {
  const [ranksBySlate, setRanksBySlate] = useState({});
  const [eloBySlate,   setEloBySlate]   = useState({});

  const slateIdsKey = items.map(({ slate }) => slate.id).join(',');

  // Rank — one game_slates/{slateId}/leaderboard/{uid} read per completed
  // game (public read, small N for any real player). elo_history is read
  // once in full and matched to a slate via its gameId field.
  useEffect(() => {
    if (!currentUser || !slateIdsKey) { setRanksBySlate({}); return; }
    const slateIds = slateIdsKey.split(',');
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(slateIds.map(async (slateId) => {
        try {
          const snap = await getDoc(doc(db, 'game_slates', slateId, 'leaderboard', currentUser.uid));
          return [slateId, snap.exists() ? (snap.data().position ?? null) : null];
        } catch (e) {
          console.error('[CompletedShowdownsTable] rank load error:', slateId, e.code, e.message);
          return [slateId, null];
        }
      }));
      if (!cancelled) setRanksBySlate(Object.fromEntries(pairs));
    })();
    return () => { cancelled = true; };
  }, [currentUser, slateIdsKey]);

  useEffect(() => {
    if (!currentUser) { setEloBySlate({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users', currentUser.uid, 'elo_history'));
        if (cancelled) return;
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.gameId) map[data.gameId] = data.eloDelta ?? null;
        });
        setEloBySlate(map);
      } catch (e) {
        console.error('[CompletedShowdownsTable] elo_history load error:', e.code, e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  return (
    <div className="completed-table-wrap">
      <table className="completed-table">
        <thead>
          <tr>
            <th>Game</th>
            <th>Date</th>
            <th>Rank</th>
            <th>Score</th>
            <th>Races</th>
            <th>ELO Change</th>
          </tr>
        </thead>
        <tbody>
          {items.map(({ slate }) => (
            <CompletedRow
              key={slate.id}
              slate={slate}
              entry={entries.find(e => e.slateId === slate.id)}
              rank={ranksBySlate[slate.id] ?? null}
              eloDelta={eloBySlate[slate.id] ?? null}
              navigate={navigate}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── ShowdownsPage ────────────────────────────────────────────────────────────

const EMPTY_STATE_COPY = {
  live: {
    title: 'No live showdowns right now.',
    body:  'Check Upcoming for games opening soon.',
  },
  upcoming: {
    title: 'No upcoming showdowns yet.',
    body:  'New showdowns are added regularly — check back soon.',
  },
  entered: {
    title: 'You have not entered a Showdown yet.',
    body:  'Browse live showdowns to build your first stable.',
    cta:   'Browse live showdowns',
    ctaFilter: 'live',
  },
  completed: {
    title: 'No completed showdowns yet.',
    body:  'Showdowns you\'ve entered will appear here once they\'re finalised.',
  },
  all: {
    title: 'No showdowns available right now.',
    body:  'Check back soon for new games.',
  },
};

export const ShowdownsPage = () => {
  const navigate = useNavigate();

  const [currentUser,  setCurrentUser]  = useState(undefined);
  const [slates,       setSlates]       = useState([]);
  const [entries,      setEntries]      = useState([]);
  const [entriesLoaded, setEntriesLoaded] = useState(false);
  const [tournaments,  setTournaments]  = useState([]);
  const [leagues,      setLeagues]      = useState([]);
  const [filter,       setFilter]       = useState('live');
  const [loading,      setLoading]      = useState(true);
  const [plannedMeetings, setPlannedMeetings] = useState([]);
  const autoTabDone = useRef(false);

  // ── Auth ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    return onAuthStateChanged(auth, user => setCurrentUser(user));
  }, []);

  // ── Load slates — SAME unfiltered fetch as the public landing page, so
  // logged-in users always see the same available games (Wellington/Wagga
  // etc), not a narrower "games I entered" slice. ────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'game_slates'), snap => {
      setSlates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // ── Load planned (admin-scheduled, not-yet-converted) meetings for the
  // Upcoming Schedule feature. Only `status: 'scheduled'` meetings render
  // here as PLANNED cards — `showdown_created` meetings already have a real
  // game_slates doc and appear through the normal slates pipeline above. ──
  useEffect(() => {
    return subscribePublicUpcomingSchedule(
      (rows) => setPlannedMeetings(rows.filter(m => m.status === 'scheduled')),
      () => {}
    );
  }, []);

  // ── Load tournaments and leagues ──────────────────────────────────────────
  useEffect(() => {
    const unsubT = onSnapshot(
      query(collection(db, 'tournaments'), orderBy('createdAt', 'desc')),
      snap => setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    const unsubL = onSnapshot(
      query(collection(db, 'leagues'), orderBy('createdAt', 'desc')),
      snap => setLeagues(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => { unsubT(); unsubL(); };
  }, []);

  // ── Load player entries ───────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) { setEntriesLoaded(false); return; }
    const q = query(
      collection(db, 'player_entries'),
      where('userId', '==', currentUser.uid)
    );
    const unsub = onSnapshot(q,
      snap => {
        setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setEntriesLoaded(true);
      },
      () => setEntriesLoaded(true)
    );
    return unsub;
  }, [currentUser]);

  // ── Derived: entry count per slate ────────────────────────────────────────
  const entryCountBySlate = useMemo(() => {
    const map = {};
    entries.forEach(e => { map[e.slateId] = (map[e.slateId] || 0) + 1; });
    return map;
  }, [entries]);

  // ── Build { slate, state } items once — single source of truth for every
  // tab/count/card below (utils/showdownStatus.js). ──────────────────────────
  const items = useMemo(
    () => buildShowdownItems(slates, entryCountBySlate),
    [slates, entryCountBySlate]
  );

  // ── Auto-select best default tab on first load ────────────────────────────
  useEffect(() => {
    if (autoTabDone.current || items.length === 0 || !entriesLoaded) return;
    autoTabDone.current = true;
    if (filterShowdownsForTab(items, 'live').length === 0) setFilter('all');
  }, [items, entriesLoaded]);

  // ── Filter + sort per tab — see filterShowdownsForTab for the exact
  // Live/Upcoming/My Entries/Completed/All rules. ────────────────────────────
  const liveItems      = useMemo(() => filterShowdownsForTab(items, 'live'),      [items]);
  const upcomingItems  = useMemo(() => filterShowdownsForTab(items, 'upcoming'),  [items]);
  const enteredItems   = useMemo(() => filterShowdownsForTab(items, 'entered'),   [items]);
  const completedItems = useMemo(() => filterShowdownsForTab(items, 'completed'), [items]);
  const allItems       = useMemo(() => filterShowdownsForTab(items, 'all'),       [items]);

  const filteredByTab = {
    live: liveItems, upcoming: upcomingItems, entered: enteredItems,
    completed: completedItems, all: allItems,
  };
  const filtered = filteredByTab[filter] || allItems;

  // ── Stats — derived from the SAME filtered lists the tabs render, so the
  // header counts can never drift from what's actually visible. ─────────────
  const liveCount    = liveItems.length;
  const upcomingCount = upcomingItems.length + plannedMeetings.length;
  const enteredCount = enteredItems.length;

  // Planned (not-yet-converted) meetings only ever surface in Upcoming/All —
  // they are not real Showdowns, so they never appear in Live/My Entries/Completed.
  const plannedForCurrentTab = (filter === 'upcoming' || filter === 'all') ? plannedMeetings : [];

  const isPageLoading = loading || (!!currentUser && !entriesLoaded);
  const emptyCopy = EMPTY_STATE_COPY[filter] || EMPTY_STATE_COPY.all;

  return (
    <div className="sd-page">

      {/* Page header */}
      <div className="sd-header">
        <div className="sd-header-left">
          <h1 className="sd-title">🏆 Showdowns</h1>
          <p className="sd-subtitle">Discover live and upcoming showdowns, and track the ones you've entered.</p>
        </div>
        <div className="sd-header-stats">
          {liveCount > 0 && (
            <div className="sd-header-stat">
              <span className="sd-header-stat-val sd-header-stat-val--live">{liveCount}</span>
              <span className="sd-header-stat-label">Live Now</span>
            </div>
          )}
          {upcomingCount > 0 && (
            <div className="sd-header-stat">
              <span className="sd-header-stat-val sd-header-stat-val--soon">{upcomingCount}</span>
              <span className="sd-header-stat-label">Opening Soon</span>
            </div>
          )}
          {enteredCount > 0 && (
            <div className="sd-header-stat">
              <span className="sd-header-stat-val">{enteredCount}</span>
              <span className="sd-header-stat-label">My Entries</span>
            </div>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="sd-filters">
        {[
          { key: 'live',        label: '🟢 Live',       count: liveCount     },
          { key: 'upcoming',    label: '🟡 Upcoming',   count: upcomingCount },
          { key: 'entered',     label: '📋 My Entries', count: enteredCount  },
          { key: 'completed',   label: '✓ Completed'                       },
          { key: 'all',         label: 'All'                                },
          { key: 'tournaments', label: '🏆 Tournaments'                    },
          { key: 'leagues',     label: '🎽 Leagues'                        },
        ].map(f => (
          <button
            key={f.key}
            className={`sd-filter-btn ${filter === f.key ? 'sd-filter-btn--active' : ''}`}
            onClick={() => setFilter(f.key)}
            type="button"
          >
            {f.label}
            {!!f.count && (
              <span className="sd-filter-count">{f.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tournaments placeholder */}
      {filter === 'tournaments' && (
        <div className="sd-placeholder">
          {tournaments.length === 0 ? (
            <div className="sd-empty">
              <span style={{ fontSize: 40 }}>🏆</span>
              <p style={{ fontWeight: 700, margin: '8px 0 4px' }}>No tournaments running</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                Tournaments feature multiple showdowns with cumulative scoring. Check back soon.
              </p>
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }}>Tournaments coming soon.</p>
          )}
        </div>
      )}

      {/* Leagues placeholder */}
      {filter === 'leagues' && (
        <div className="sd-placeholder">
          {leagues.length === 0 ? (
            <div className="sd-empty">
              <span style={{ fontSize: 40 }}>🎽</span>
              <p style={{ fontWeight: 700, margin: '8px 0 4px' }}>No leagues yet</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                Leagues let you compete in a private group across a season. Check back soon.
              </p>
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }}>Leagues coming soon.</p>
          )}
        </div>
      )}

      {/* Card grid — only for non-placeholder tabs */}
      {filter !== 'tournaments' && filter !== 'leagues' && (isPageLoading ? (
        <div className="sd-loading">
          <div className="ss-spinner" />
          <p>Loading showdowns…</p>
        </div>
      ) : (filtered.length === 0 && plannedForCurrentTab.length === 0) ? (
        <div className="sd-empty">
          <span style={{ fontSize: 40 }}>🏇</span>
          <p style={{ fontWeight: 700, margin: '8px 0 4px' }}>{emptyCopy.title}</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{emptyCopy.body}</p>
          {emptyCopy.cta && (
            <button className="sd-btn sd-btn--outline"
                    onClick={() => setFilter(emptyCopy.ctaFilter)} type="button">
              {emptyCopy.cta}
            </button>
          )}
        </div>
      ) : filter === 'completed' ? (
        <CompletedShowdownsTable
          items={filtered}
          entries={entries}
          currentUser={currentUser}
          navigate={navigate}
        />
      ) : (
        <div className="sd-grid">
          {filtered.map(({ slate, state }) => (
            <ShowdownCard
              key={slate.id}
              slate={slate}
              state={state}
              myEntryCount={entryCountBySlate[slate.id] || 0}
              navigate={navigate}
            />
          ))}
          {plannedForCurrentTab.map((meeting) => (
            <PlannedMeetingCard key={meeting.meetingKey} meeting={meeting} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default ShowdownsPage;
